import { cloneDeep, filter, isEmpty, isEqual } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';

import { GlobalFilters } from 'types';
import { SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import {
  DataSourceMetadata,
  FilterItemType,
  ParameterConfiguration,
  ReportDataSource,
} from 'common/types/reportFilters';
import type { ClientContextVariable } from 'common/types/clientContextVariable';
import { dataSourceReducerSelector, getHasGlobalFilter } from 'store/TopLevelSelector';
import { groupClientContextVariablesByDataset } from 'common/components/FilterBar/lib/Filters';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { inPrintMode } from 'util/in-print-mode';

export const getReportDataSource = createSelector(
  [dataSourceReducerSelector],
  ({ dataSourceList, filterParameterConfigurations }) => {
    const dataSource: ReportDataSource = {
      dataSourceList: dataSourceList ?? [],
    };

    if (filterParameterConfigurations !== undefined) {
      dataSource.filterParameterConfigurations = filterParameterConfigurations;
    }

    return dataSource;
  },
);

export const selectParameters = createSelector(
  [dataSourceReducerSelector],
  ({ parameters }) => parameters ?? {},
);

export const getDataSourceList = createSelector([getReportDataSource], (reportDataSource) => {
  const { dataSourceList = [] } = (reportDataSource || {}) as ReportDataSource;
  return dataSourceList;
});

export const getDataSourceUids = createSelector([getDataSourceList], (dataSourceList) =>
  dataSourceList.map(({ datasetUid }) => datasetUid),
);

export const getFilterParameterConfigurations = createSelector(
  dataSourceReducerSelector,
  ({ filterParameterConfigurations }) => filterParameterConfigurations ?? [],
);

export const getDraftFilterParameterConfigurations = createSelector(
  dataSourceReducerSelector,
  ({ draftFilterParameterConfigurations }) => draftFilterParameterConfigurations ?? [],
);

export const getHasUserMadeFilterChanges = createSelector(
  getFilterParameterConfigurations,
  getDraftFilterParameterConfigurations,
  (applied, draft) => !isEqual(applied, draft),
);

export const getGlobalFilters = createSelector(
  [getFilterParameterConfigurations],
  (filterParameterConfigurations): GlobalFilters => {
    if (isEmpty(filterParameterConfigurations)) {
      return {};
    }

    const globalFilters: GlobalFilters = {};
    filterParameterConfigurations.forEach((filterParameterConfig) => {
      if (filterParameterConfig.type === FilterItemType.FILTER) {
        const { columns } = filterParameterConfig.config;
        columns.forEach((column) => {
          const datasetUid = column.datasetUid;
          const vifFilter = cloneDeep({
            ...filterParameterConfig.config,
            columns: filter(
              filterParameterConfig.config.columns,
              (filterColumn) => filterColumn.datasetUid === datasetUid,
            ),
          }) as SoqlFilter;

          if (Array.isArray(globalFilters[datasetUid])) {
            (globalFilters[datasetUid] as SoqlFilter[]).push(vifFilter as SoqlFilter);
          } else {
            globalFilters[datasetUid] = [vifFilter] as SoqlFilter[];
          }
        });
      }
    });

    return globalFilters;
  },
);

export const getParameterOverrides = createSelector(
  [getFilterParameterConfigurations],
  (filterParameterConfigurations) => {
    if (filterParameterConfigurations?.length) {
      const parameterConfigurations = filterParameterConfigurations
        .filter(({ type }) => type === FilterItemType.PARAMETER)
        .map(({ config }) => config as ParameterConfiguration);
      return groupClientContextVariablesByDataset(parameterConfigurations);
    }

    return {};
  },
);

export const hasEmptyDataSource = createSelector(dataSourceReducerSelector, (state) => {
  return isEmpty(state.dataSourceList);
});

export const getDataSourceForSerialize = createSelector(
  [getReportDataSource, hasEmptyDataSource],
  (dataSource, isEmptyDataSource) => {
    if (isEmptyDataSource) {
      return undefined;
    }
    return dataSource;
  },
);

export const getRequiredDataSources = createSelector(
  [dataSourceReducerSelector],
  ({ requiredDataSources }) => requiredDataSources || [],
);

export const getDataSourceMetadata = createSelector(
  [dataSourceReducerSelector, getDataSourceUids],
  ({ dataSourceMetadata }, dataSources) => {
    const applicableMetadata: DataSourceMetadata = {};
    const allMetadata = dataSourceMetadata ?? {};
    dataSources.forEach((datasetUid) => {
      if (allMetadata[datasetUid]) {
        applicableMetadata[datasetUid] = allMetadata[datasetUid];
      }
    });
    return applicableMetadata;
  },
);

export const getAllDataSourceCCVs = createSelector(
  [selectParameters, getDataSourceUids],
  (parameters, dataSources) => {
    const availableParameters = Object.keys(parameters)
      .filter((datasetUid) => dataSources.includes(datasetUid))
      .reduce(
        (acc, datasetUid) => {
          acc[datasetUid] = parameters[datasetUid];
          return acc;
        },
        {} as Record<string, ClientContextVariable[]>,
      );

    return availableParameters;
  },
);

export const getIsNewRequiredSourcesAdded = createSelector(
  dataSourceReducerSelector,
  (state) => state.isNewRequiredSourcesAdded || false,
);

export const getShowLoadingIndicator = createSelector(
  dataSourceReducerSelector,
  (state) => state.showLoadingIndicator || false,
);

/**
 * Returns true if the report has been run, or automatically returns true when:
 *  - there's no Global Filter Bar (there are no filters that need to be applied via "Run Report".)
 *  - page is in edit mode
 *  - page is in print mode
 * Returning true enables visualizations to auto-load.
 */
export const getHasReportBeenRun = createSelector(
  [dataSourceReducerSelector, getHasGlobalFilter],
  (dataSourceState, hasGlobalFilter) => {
    const hasReportBeenRun = dataSourceState.hasReportBeenRun || false;
    const StorytellerEnvironment = EnvironmentFactory();

    if (hasReportBeenRun) {
      return true;
    }

    if (!hasGlobalFilter || isEmpty(dataSourceState.filterParameterConfigurations)) {
      // If no GFB exists, auto-load visualizations by returning true
      return true;
    }

    if (StorytellerEnvironment.EDIT_MODE) {
      // if in edit mode, auto-load visualizations by returning true
      return true;
    }

    if (inPrintMode() || StorytellerEnvironment.LOADED_AS_EMAIL) {
      // if in print mode, or email-mode, auto-load visualizations by returning true
      return true;
    }

    return false;
  },
);
